/**
 * RaceDayCtaClient — Role-Based Call-to-Action for Race Day Card
 *
 * Client component that determines the viewer's role using the
 * UserData context and renders the appropriate call-to-action button:
 *
 *   - Registered pilot → "Open my counter" linking to the counter page
 *   - Officer/Admin → "Race day mobile tools" linking to the mobile utility
 *   - Signed-in member (not registered) → "Open leaderboard"
 *   - Anonymous → "Open leaderboard"
 *
 * Pilot registration detection queries Firestore client-side to check
 * if the user's linked membership maps to a pilot registered for the
 * given race.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Gauge, Wrench, Trophy, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useUserData } from '@/hooks/use-user-data';
import { useFirestore } from '@/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from 'firebase/firestore';
import type { RaceDayCardRace } from './home-race-day-card';

/**
 * Props for the {@link RaceDayCtaClient} component.
 */
interface RaceDayCtaClientProps {
  /** Serialized race data from the server component. */
  race: RaceDayCardRace;
}

/**
 * Resolved pilot information for a registered pilot.
 */
interface PilotMatch {
  /** The Firestore pilot document ID. */
  pilotId: string;
}

/**
 * RaceDayCtaClient renders the role-appropriate CTA button for the
 * race day card. It checks auth state, officer role, and pilot
 * registration to determine which tier to display.
 *
 * @param props - Component props containing the serialized race data.
 * @returns A CTA button appropriate to the viewer's role.
 */
export function RaceDayCtaClient({ race }: RaceDayCtaClientProps) {
  const {
    loading,
    isAuthenticated,
    isOfficer,
    isAdmin,
    userDoc,
  } = useUserData();

  const firestore = useFirestore();
  const [pilotMatch, setPilotMatch] = useState<PilotMatch | null>(null);
  const [pilotChecked, setPilotChecked] = useState(false);

  // Check if the current user is a registered pilot for this race
  useEffect(() => {
    if (!isAuthenticated || !userDoc?.membershipId) {
      setPilotChecked(true);
      return;
    }

    let cancelled = false;

    async function checkRegistration() {
      try {
        // Find pilot linked to this user's membership
        const pilotsQuery = query(
          collection(firestore, 'pilots'),
          where('memberId', '==', userDoc!.membershipId),
          limit(1),
        );
        const pilotSnap = await getDocs(pilotsQuery);
        if (cancelled || pilotSnap.empty) {
          if (!cancelled) setPilotChecked(true);
          return;
        }

        const pilotId = pilotSnap.docs[0].id;

        // Check if this pilot is registered for today's race
        const regQuery = query(
          collection(firestore, 'raceEventRegistrations'),
          where('raceEventId', '==', race.id),
          where('pilotId', '==', pilotId),
          limit(1),
        );
        const regSnap = await getDocs(regQuery);

        if (!cancelled) {
          if (!regSnap.empty) {
            setPilotMatch({ pilotId });
          }
          setPilotChecked(true);
        }
      } catch (err) {
        console.error('[RaceDayCtaClient] registration check failed:', err);
        if (!cancelled) setPilotChecked(true);
      }
    }

    checkRegistration();
    return () => { cancelled = true; };
  }, [isAuthenticated, userDoc?.membershipId, race.id, firestore]);

  // Show a subtle loading state while resolving role
  if (loading || !pilotChecked) {
    return (
      <Button disabled variant="outline" className="w-full sm:w-auto">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  // Tier 1: Registered pilot → contestant mobile app. /m/contestant
  // owns the active-heat redirect, the join-race detail view, and
  // the counter — sending the pilot to the desktop scorecard PDF
  // instead (the previous behaviour) just dropped them at a static
  // export of the score sheet.
  if (pilotMatch) {
    return (
      <Button asChild className="w-full sm:w-auto">
        <Link href="/m/contestant">
          <Gauge className="w-4 h-4 mr-2" />
          Open my counter
        </Link>
      </Button>
    );
  }

  // Tier 2: Officer/Admin → mobile race-admin tools. The desktop
  // race admin page works on a phone but the mobile sub-app
  // (heat control, pilot QRs, scorecard) is the operator-friendly
  // surface.
  if (isOfficer || isAdmin) {
    return (
      <Button asChild className="w-full sm:w-auto">
        <Link href={`/m/race-admin/${race.id}/control`}>
          <Wrench className="w-4 h-4 mr-2" />
          Race day mobile tools
        </Link>
      </Button>
    );
  }

  // Tier 3 & 4: Member (not registered) or Anonymous → leaderboard
  return (
    <Button asChild variant="secondary" className="w-full sm:w-auto">
      <Link href="/competitions">
        <Trophy className="w-4 h-4 mr-2" />
        Race in progress &mdash; open leaderboard
      </Link>
    </Button>
  );
}
